import { ApplicationElement, html } from '../application_element/element.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { chatConfig } from '../chat/lib/config.js';

import translations from '../chat_message_new/translations.js';

import '../icon/element.js';
import '../attachment/element.js';
import '../online_status/element.js';
import '../avatar/element.js';
import '../truncate/element.js';
import '../chat_message_menu/element.js';
import '../emoji_detector/element.js';
import ServerTime from '../../lib/server_time.js';

import { safeFormattedMessageBody } from '../../lib/safe_formatted_message_body.js';

import { ChatMessageEvent } from './lib/chat_message_event.js';

const GRACE_PERIODS = {
  edit: 1000 * 60 * 15, // 15 minutes;
  destroy: 1000 * 60 * 60 * 24 * 7, // 7 days;
  restore: 1000 * 60 * 60 * 1, // 1 hours;
};

export default class ChatMessageBase extends ApplicationElement {
  static translations = translations;

  static properties = {
    userName: { type: String },
    userPicture: { type: String },
    userId: { type: Number },
    userUrl: { type: String },

    my: { type: Boolean, reflect: true },
    seen: { type: Boolean },
    isRepresentative: { type: Boolean },
    isCurrentUserAdmin: { type: Boolean },
    errors: { type: String },
    attachment: { type: Object },

    images: { type: Object },
    uploading: { type: Boolean },
    editMode: { type: Boolean },

    imageWidth: { type: Number },
    imageHeight: { type: Number },

    // New properties
    chatMembersCount: { type: Number },
    currentUser: { type: Object },
    readIds: { type: Set },
    showUserName: { type: Boolean },
    uuid: { type: String },
    deletedByUserName: { type: String },
    isChatDisabled: { type: Boolean },

    savedAt: { type: Date },
    createdAt: { type: Date },
    editedAt: { type: Date },
    deletedAt: { type: Date },
    receivedAt: { type: Date },
    seenAt: { type: Date },
    sentAt: { type: Date },
    uploadProgress: { type: Number },
    body: { type: String },

    // Reply message properties
    parentMessage: { type: Object },
    menuOpen: { type: Boolean, reflect: true },
  };

  static STATES = {
    0: 'initialized',
    1: 'sending',
    2: 'sent',
    3: 'persisted',
    100: 'error',
  };

  static ICONS = {
    initialized: chatConfig.icons.states.initialized,
    sending: chatConfig.icons.states.sending,
    sent: chatConfig.icons.states.sent,
    persisted: chatConfig.icons.states.persisted,
    final_seen: chatConfig.icons.states.final_seen,
    error: chatConfig.icons.states.error,
  };

  menuRef = createRef();

  willUpdate(changedProperties) {
    if (changedProperties.has('body') && this.body) {
      this.formattedBody = safeFormattedMessageBody(this.body);
    }
  }

  get menu() {
    return this.menuRef.value;
  }

  get state() {
    if (this.savedAt) return 3;
    if (this.receivedAt) return 2;
    if (this.errors) return 100;

    if (this.unsent) return 200;
    if (this.sentAt) return 1;

    return 0;
  }

  get hasBody() {
    return !!this.body;
  }

  get finalSeen() {
    return (
      this.seen || (this.my && this.readIds?.size >= this.chatMembersCount)
    );
  }

  /** @internal */
  get isValid() {
    return !this.errors && !this.destroyed;
  }

  /** @internal */
  get copyOn() {
    return this.hasBody && this.isValid;
  }

  /** @internal */
  get visitProfileOn() {
    return !this.my && this.isValid;
  }

  get infoOn() {
    return chatConfig.menu.infoOn && this.my && this.isValid;
  }

  /** @internal */
  get replyOn() {
    return this.isValid && !this.isChatDisabled;
  }

  /** @internal */
  get editOn() {
    return (
      this.canBeManipulated('edit') &&
      this.my &&
      !this.destroyed &&
      !this.isChatDisabled
    );
  }

  /** @internal */
  get deleteOn() {
    return (
      this.canBeManipulated('destroy') &&
      !this.destroyed &&
      !this.isChatDisabled
    );
  }

  /** @internal */
  get restoreOn() {
    return (
      this.canBeManipulated('restore') &&
      this.my &&
      this.destroyed &&
      !this.isChatDisabled
    );
  }

  /** @internal */
  get menuOn() {
    return (
      !this.editMode &&
      !this.unsent &&
      (this.copyOn ||
        this.visitProfileOn ||
        this.editOn ||
        this.deleteOn ||
        this.restoreOn ||
        this.replyOn)
    );
  }

  get hasParentMessage() {
    return this.parentMessage?.uuid;
  }

  /** @internal */
  isWithinGracePeriod(action) {
    const attribute = action === 'restore' ? 'deletedAt' : 'createdAt';
    return ServerTime.now() - this[attribute] < GRACE_PERIODS[action];
  }

  /** @internal */
  canBeManipulated(action) {
    return (
      !this.errors &&
      this.isWithinGracePeriod(action) &&
      (this.my || this.isCurrentUserAdmin)
    );
  }

  onPictureClick(e) {
    this.dispatchEvent(
      new CustomEvent('message-image-clicked', {
        detail: { realTarget: e.target },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** @internal */
  get rawStateName() {
    return this.constructor.STATES[this.state];
  }

  /** @internal */
  get stateName() {
    return this.t(this.rawStateName, { scope: 'states' });
  }

  /** @internal */
  get stateIcon() {
    return this.constructor.ICONS[this.rawStateName];
  }

  /** @internal */
  get pictureStyle() {
    const ratio = this.imageWidth / this.imageHeight;
    const isLandscape = this.imageWidth >= this.imageHeight;

    return {
      width: isLandscape ? `${this.imageWidth}px` : null,
      height: isLandscape ? null : `${this.imageHeight}px`,
      'aspect-ratio': ratio,
    };
  }

  /** @internal */
  get destroyed() {
    return this.deletedAt;
  }

  /** @internal */
  get bodyOn() {
    return this.destroyed || this.hasBody;
  }

  /** @internal */
  get attachmentOn() {
    return this.attachment && !this.destroyed;
  }

  /** @internal */
  get classes() {
    return {
      content: true,
      edit: this.editMode,
      error: this.errors,
      deleted: this.destroyed,
      my: this.my,
      'has-sender': this.showUserName,
      'has-body': this.bodyOn,
      'has-attachment': this.attachmentOn,
      'has-picture': this.attachmentOn && this.fileType === 'image',
      'has-video': this.attachmentOn && this.fileType === 'video',
      'has-audio': this.attachmentOn && this.fileType === 'audio',
      'has-file': this.attachmentOn && this.fileType === 'file',
    };
  }

  onDelete() {
    this.dispatchEvent(new ChatMessageEvent('delete', this));
  }

  onRestore() {
    this.dispatchEvent(new ChatMessageEvent('restore', this));
  }

  onEdit() {
    this.dispatchEvent(new ChatMessageEvent('edit', this));
  }

  onRetry() {
    this.dispatchEvent(new ChatMessageEvent('retry', this));
  }

  onDismiss() {
    this.dispatchEvent(new ChatMessageEvent('dismiss', this));
  }

  onMenuOpen() {
    this.menuOpen = true;
  }

  onMenuClose() {
    this.menuOpen = false;
  }

  /** @internal */
  get fileType() {
    if (!this.attachment) return;

    return (
      (this.attachment.type.includes('image') && 'image') ||
      (this.attachment.type.includes('video') && 'video') ||
      (this.attachment.type.includes('audio') && 'audio') ||
      'file'
    );
  }

  renderMenu() {
    return html`<ef-chat-message-menu
      .message=${this}
      .messageId=${this.uuid}
      .infoOn=${this.infoOn}
      .copyOn=${this.copyOn}
      .visitProfileOn=${this.visitProfileOn}
      .editOn=${this.editOn}
      .deleteOn=${this.deleteOn}
      .restoreOn=${this.restoreOn}
      .replyOn=${this.replyOn}
      ${ref(this.menuRef)}
      @dropdown-opened=${this.onMenuOpen}
      @dropdown-closed=${this.onMenuClose}>
    </ef-chat-message-menu>`;
  }

  renderDestroyed() {
    return html`
      <span class="message-deleted">
        <ef-icon name=${chatConfig.icons.message_deleted}></ef-icon>
        <span>${this.t('deleted', { userName: this.deletedByUserName })}</span>
        <span class="spacer"></span>
      </span>
    `;
  }

  renderMessageError() {
    return html` <div class="message-error">${this.errors}</div> `;
  }

  renderAttachment() {
    if (this.state === 100) {
      return this.renderMessageErrorAttachment();
    }

    switch (this.fileType) {
      case undefined:
        return null;
      case 'image':
        return this.images ? this.renderPicture() : null;

      // NOT YET SUPPORTED as we need to implement streaming solution
      // case 'video':
      //   return this.renderVideo();
      // case 'audio':
      //   return this.renderAudio();
      default:
        return this.renderFile();
    }
  }

  renderMessageErrorAttachment() {
    return html`<div class="error-attachment">
      ${this.attachment.originalFilename}<span class="spacer"></span>
    </div>`;
  }

  renderPicture() {
    return html`
      <picture class="attachment picture">
        <img
          @click=${this.onPictureClick}
          src=${this.images.imageSmall}
          srcset=${`${this.images.imageMedium} 2x`}
          style=${styleMap(this.pictureStyle)}
          data-url=${this.images.imageLarge}
          data-title=${this.attachment.originalFilename}
          data-download-url="${this.attachment.downloadUrl}"
          alt=${this.t('image_alt')} />
      </picture>
    `;
  }

  renderFile() {
    return html`
      <div class="attachment file">
        <ef-attachment
          file=${this.attachment.originalFilename}
          href=${this.attachment.url}
          layout=${chatConfig.fileLayout}>
        </ef-attachment>
      </div>
    `;
  }

  renderAudio() {
    return html`
      <div class="attachment audio">
        <audio controls>
          <source src=${this.attachment.url} />
        </audio>
      </div>
    `;
  }

  renderVideo() {
    return html`
      <div class="attachment video">
        <video controls>
          <source src=${this.attachment.url} />
        </video>
      </div>
    `;
  }

  renderAvatar() {
    return html`
      <a aria-label=${ifDefined(this.userName)} href=${this.userUrl}>
        <div class="avatar">
          <ef-online-status uid="${this.userId}"></ef-online-status>
          <ef-avatar
            ?disableInitials=${chatConfig.avatar.disableInitials}
            picture=${ifDefined(this.userPicture)}
            name="${this.userName}"
            layout="xs"
            rounded="true"></ef-avatar>
        </div>
      </a>
    `;
  }

  renderName() {
    if (!this.showUserName) return null;

    return html`
      <div class="sender">
        <span>${this.userName}</span>
        ${!this.isRepresentative
          ? null
          : html`<ef-icon
              name=${chatConfig.icons.official_representative}
              title=${this.t('official_representative')}>
            </ef-icon>`}
      </div>
    `;
  }

  renderCreatedAt() {
    if (!this.createdAt) return null;

    return html`
      <div class="created-at">
        ${this.i18n.l('time.formats.compact', this.createdAt)}
      </div>
    `;
  }

  renderStatusIndicatorIcon() {
    return html`
      <ef-icon
        name=${this.finalSeen
          ? this.constructor.ICONS.final_seen
          : this.stateIcon}
        class=${classMap({ seen: this.finalSeen })}>
      </ef-icon>
    `;
  }

  renderStatusIndicator() {
    return html`
      <span class="status-indicator">
        ${this.renderStatusIndicatorIcon()}
        ${this.my && this.state === 3 ? this.renderStatusIndicatorIcon() : null}
      </span>
    `;
  }

  renderMessageSlot() {
    return html`
      <ef-truncate maxLines=${chatConfig.truncateMaxLines} class="body">
        <ef-emoji-detector
          class="truncate-content"
          text=${this.formattedBody}></ef-emoji-detector>
        ${this.renderEdited()}
        <span class="spacer"></span>
      </ef-truncate>
    `;
  }

  renderMessageBody() {
    return html`
      ${this.destroyed ? this.renderDestroyed() : this.renderMessageSlot()}
    `;
  }

  renderEdited() {
    if (!this.editedAt) return null;

    return html`
      <span
        class="message-edited"
        title=${this.i18n.l('time.formats.short', this.editedAt)}>
        (${this.t('edited')})
      </span>
    `;
  }

  renderOriginalMessage() {
    const { parentMessage } = this;

    return html`
      <ef-chat-message-reply
        .uuid=${parentMessage.uuid}
        .body=${parentMessage.body}
        .my=${parentMessage.userId === this.currentUser.id}
        .isRepresentative=${parentMessage.isUserOfficial}
        .userName=${parentMessage.userName}
        .userPicture=${parentMessage.userPicture}
        .userUrl=${parentMessage.userUrl}
        .userId=${parentMessage.userId}
        .attachment=${parentMessage.attachment}
        .readIds=${parentMessage.readIds}
        .unsent=${parentMessage.unsent}
        .images=${parentMessage.images}
        .imageWidth=${parentMessage.imageWidth}
        .imageHeight=${parentMessage.imageHeight}
        .errors=${parentMessage.errors}
        .savedAt=${parentMessage.savedAt}
        .createdAt=${parentMessage.createdAt}
        .editedAt=${parentMessage.editedAt}
        .deletedAt=${parentMessage.deletedAt}
        .deletedByUserName=${parentMessage.deletedByUserName}
        .receivedAt=${parentMessage.receivedAt}>
      </ef-chat-message-reply>
    `;
  }

  renderProgress() {
    return html`<progress
      value=${this.uploadProgress || 0}
      max="100"></progress>`;
  }

  renderUnsentActions() {
    return html`
      <div class="actions">
        ${!this.attachment && !this.isChatDisabled
          ? html`<button
              type="button"
              @click=${this.onRetry}
              aria-label=${this.t('retry')}>
              <ef-icon name=${chatConfig.icons.retry}></ef-icon>
            </button>`
          : null}
        <button
          type="button"
          @click=${this.onDismiss}
          aria-label=${this.t('dismiss')}>
          <ef-icon name=${chatConfig.icons.dismiss}></ef-icon>
        </button>
      </div>
    `;
  }

  renderTooltip() {
    return html`
      <span class="tooltip">
        ${this.finalSeen
          ? this.t('states.seen')
          : this.t(`states.${this.rawStateName}`)}
      </span>
    `;
  }

  render() {
    throw new Error('Implement render method in your class');
  }
}

ChatMessageBase.extendTranslationsAs('ef-chat-message-new');
